import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Phone, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import PromotionBanner from './PromotionBanner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [contactInfo, setContactInfo] = useState({
    phone: '+381 65 46 000 46',
    email: 'kontakt@fotoexpres.rs',
    workingHours: 'Pon-Pet: 09:00-18:00'
  });
  const [freeDeliveryLimit, setFreeDeliveryLimit] = useState(5000);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.settings) {
        setContactInfo({
          phone: response.data.settings.contactPhone || '+381 65 46 000 46',
          email: response.data.settings.contactEmail || 'kontakt@fotoexpres.rs',
          workingHours: response.data.settings.workingHours || 'Pon-Pet: 09:00-18:00'
        });
        setFreeDeliveryLimit(response.data.settings.freeDeliveryLimit || 5000);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  return (
    <>
      {/* Promotion Banner - Above everything */}
      <PromotionBanner />
      
      <div className="bg-gray-900 text-white py-3 px-6">
        {/* Desktop Layout */}
        <div className="hidden md:flex max-w-7xl mx-auto justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Phone size={16} />
              <span>{contactInfo.phone}</span>
            </div>
            <span className="text-gray-400">{contactInfo.workingHours}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-400">Besplatna dostava za porudžbine preko {freeDeliveryLimit} RSD</span>
          </div>
        </div>
        
        {/* Mobile Layout - Stacked */}
        <div className="md:hidden flex flex-col gap-2 text-xs text-center">
          <div className="flex items-center justify-center gap-2">
            <Phone size={14} />
            <span>{contactInfo.phone}</span>
          </div>
          <div className="text-gray-400">{contactInfo.workingHours}</div>
          <div className="text-gray-400">Besplatna dostava preko {freeDeliveryLimit} RSD</div>
        </div>
      </div>
      
      <nav className="bg-white shadow-md sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2 md:py-3">
          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Mobile Header - Logo + Menu Button */}
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center">
                <div className="bg-orange-600 px-2 py-1 rounded-lg">
                  <img 
                    src="/images/logo.png" 
                    alt="Fotoexpres Logo" 
                    className="h-8 w-auto"
                  />
                </div>
              </Link>
              
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X size={24} className="text-gray-700" />
                ) : (
                  <Menu size={24} className="text-gray-700" />
                )}
              </button>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="absolute left-0 right-0 top-full bg-white border-b border-gray-200 shadow-lg">
                <div className="flex flex-col py-2">
                  <Link 
                    to="/" 
                    className="px-4 py-3 hover:bg-gray-50 text-gray-700 font-medium border-b border-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Početna
                  </Link>
                  <Link 
                    to="/proizvodi" 
                    className="px-4 py-3 hover:bg-gray-50 text-gray-700 font-medium border-b border-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Proizvodi
                  </Link>
                  <Link 
                    to="/prices" 
                    className="px-4 py-3 hover:bg-gray-50 text-gray-700 font-medium border-b border-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Cenovnik
                  </Link>
                  <Link 
                    to="/faq" 
                    className="px-4 py-3 hover:bg-gray-50 text-gray-700 font-medium border-b border-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAQ
                  </Link>
                  <div className="px-4 py-3">
                    <Link to="/upload" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white gap-2 font-medium">
                        <Upload size={16} />
                        Pošalji Fotografije
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="bg-orange-600 px-3 py-1 rounded-lg">
                <img 
                  src="/images/logo.png" 
                  alt="Fotoexpres Logo" 
                  className="h-10 w-auto"
                />
              </div>
            </Link>

            <div className="flex items-center gap-8 text-gray-700 font-medium text-sm uppercase tracking-wide">
              <Link to="/" className="hover:text-orange-600 transition-colors">Početna</Link>
              <Link to="/proizvodi" className="hover:text-orange-600 transition-colors">Proizvodi</Link>
              <Link to="/prices" className="hover:text-orange-600 transition-colors">Cenovnik</Link>
              <Link to="/faq" className="hover:text-orange-600 transition-colors">FAQ</Link>
            </div>

            <div className="flex items-center gap-2">
              <Link to="/upload">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white gap-2 font-medium text-sm px-4 py-2">
                  <Upload size={18} />
                  Pošalji Fotografije
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;